import { s, type Schema } from '@w/schema'
import { sqliteTable } from '@w/db/sqlite'
import { index } from '@w/db'
import type { Blocks } from '@w/content-blocks'
import { languageSchema, lexiconEntrySchema, type Language, type LexiconEntry } from '../../lexicon/lexicon-model'
import { isPageKind, type PageKind } from './page-kind-policy'
export * from './page-kind-policy'
export * from './directory-tables'
import { directoryTables } from './directory-tables'
// Synchronization coordinate only; policy remains in replicated product rows.
export const securityRevisions = sqliteTable({
  columns: c => ({ workspace: c.text().notNull().primaryKey(), revision: c.integer().notNull() }),
  fn: () => ({ secure: () => true }),
})
/** Incremental fold of the immutable transaction log: how far it is folded, keyed by the folded tail frame. */
export const historyFolds = sqliteTable({
  columns: c => ({ workspace: c.text().notNull().primaryKey(), revision: c.integer().notNull(), generation: c.integer().notNull(), owner: c.text().notNull(), digest: c.blob().notNull() }),
  fn: () => ({ secure: () => true }),
})
/** Log revisions the fold keeps: `catalog` frames change the custom catalog, `rows` frames carry custom-table rows. */
export const historyFoldFrames = sqliteTable({
  columns: c => ({ workspace: c.text().notNull(), revision: c.integer().notNull(), kind: c.text().notNull().$type<'catalog' | 'rows'>() }),
  primaryKey: t => [t.workspace, t.revision],
  fn: () => ({ secure: () => true }),
})
/** Last view per user and page (page-views.spec.md). Server-owned, never replicated: a view is not an edit. */
export const pageViews = sqliteTable({
  columns: c => ({ workspaceId: c.ulid().notNull(), pageId: c.text().notNull(), userId: c.ulid().notNull(), viewedAt: c.integer().notNull() }),
  primaryKey: t => [t.workspaceId, t.pageId, t.userId],
  fn: () => ({ secure: () => true }),
})
/** Retained-parts bound (replication/retained-parts-bound.ts): kept question parts, indexed at admission from retainedParts rows. */
export const retainedIndex = sqliteTable({
  columns: c => ({ workspace: c.text().notNull(), pageId: c.text().notNull(), qid: c.text().notNull(), node: c.text().notNull(), content: c.text().notNull(), removedBy: c.text().notNull(), removedAt: c.integer().notNull() }),
  primaryKey: t => [t.workspace, t.pageId, t.qid, t.node],
  fn: () => ({ secure: () => true }),
})
/** Retained-parts bound: per stored learner card, the columns that decide whether its review is resolved. */
export const cardResolution = sqliteTable({
  columns: c => ({ workspace: c.text().notNull(), userId: c.text().notNull(), qid: c.text().notNull(), pageId: c.text(), touched: c.integer().notNull().default(0), deletedAt: c.integer(), deletedBy: c.text(), acceptedRemoval: c.text() }),
  primaryKey: t => [t.workspace, t.userId, t.qid],
  indexes: t => [index(t.workspace, t.qid)],
  fn: () => ({ secure: () => true }),
})
/** The generation each production reset left per workspace and owner (content-reset-sql.ts); below it a replica holds pre-reset data. */
export const contentResets = sqliteTable({
  columns: c => ({ workspace: c.text().notNull(), owner: c.text().notNull(), generation: c.integer().notNull() }),
  primaryKey: t => [t.workspace, t.owner],
  fn: () => ({ secure: () => true }),
})
/** Releases whose production reset ran (scripts/cleanup-production.ts `--release`, run locally by the operator); each resets once. */
export const contentResetReleases = sqliteTable({
  columns: c => ({ release: c.text().notNull(), resetAt: c.integer().notNull() }),
  primaryKey: t => [t.release],
  fn: () => ({ secure: () => true }),
})
export const serverTables = { ...directoryTables, _flashcards_security_revisions: securityRevisions, _flashcards_history_folds: historyFolds, _flashcards_history_fold_frames: historyFoldFrames, pageViews, _flashcards_retained: retainedIndex, _flashcards_card_resolution: cardResolution, _flashcards_content_resets: contentResets, _flashcards_content_reset_releases: contentResetReleases }
export type PageContent =
  | { kind: 'note'; children: Blocks }
  | { kind: 'language'; language: Language; children: Blocks }
  | { kind: 'lexiconEntry'; entry: LexiconEntry }
  // System subpage under a language page. It CONTAINS (entries are rows with
  // canonical child edge from its id); it never STORES them (Core 3).
  | { kind: 'vocabulary' }
  // Retired marker for Grammar; lexicon adoption turns such a page into a note (flashcards-lexicon.spec.md).
  | { kind: 'grammar' }
  | { kind: 'databaseRow'; children: Blocks }
  // A member's personal root (db-crdt-sync.spec.md root-pages-have-ulid-ids): a page read like any other, with no blocks of its own.
  | { kind: 'personalRoot'; children: Blocks }

function isPageContentShape(data: unknown): data is PageContent {
  if (!isRecord(data)) return false
  if (!('kind' in data)) return Array.isArray(data.children)
  if (data.kind === 'note' || data.kind === 'databaseRow' || data.kind === 'personalRoot') return Array.isArray(data.children)
  if (data.kind === 'language')
    return (
      languageSchema.safeParse(data.language).success &&
      Array.isArray(data.children)
    )
  if (data.kind === 'lexiconEntry') {
    return lexiconEntrySchema.safeParse(data.entry).success
  }
  if (data.kind === 'vocabulary' || data.kind === 'grammar') return true
  return false
}

/**
 * The kind a content patch declares, if any. A note patch from the editor
 * carries only `children` ops and declares no kind, so this returns undefined
 * and the fold keeps merging into whatever the page already is.
 */
export function readPageContentKind(value: unknown): PageKind | undefined {
  if (!isRecord(value) || !isPageKind(value.kind)) return undefined
  return value.kind
}

export const pageContentSchema: Schema<PageContent> = s.custom<PageContent>(
  isPageContentShape,
  'Invalid page content',
)

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
